import { ChangeDetectionStrategy, Component, inject, input, output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ZagIcon } from '../icon/icon';
import { ColorContrastService } from '../services/color-contrast.service';

/**
 * Data structure for SkillListRow component
 */
export interface SkillListRowData {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  icon?: string | null;
  /** Optional flag or image URL — when present renders as an <img> instead of the icon avatar. */
  imageUrl?: string | null;
}

/**
 * SkillListRow Component
 *
 * Displays a skill's information in a horizontal row format for list views.
 * Shows name, description, and action icons.
 *
 * @example
 * ```html
 * <zag-skill-list-row
 *   [skill]="skillData"
 *   [editRoute]="['/skills-catalog', skillData.id]"
 *   (rowClick)="onSkillSelected($event)"
 *   (deleteClick)="onDeleteSkill($event)">
 * </zag-skill-list-row>
 * ```
 */
@Component({
  selector: 'zag-skill-list-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ZagIcon],
  host: {
    '[class.zag-skill-list-row]': 'true',
    '[attr.role]': '"button"',
    '[attr.tabindex]': '0',
    '(click)': 'onRowClick()',
    '(keydown)': 'onRowKeydown($event)',
  },
  template: `
    <div class="zag-skill-list-row__content">
      @if (skill().imageUrl) {
        <img class="zag-skill-list-row__flag" [src]="skill().imageUrl" [alt]="skill().name" aria-hidden="true" />
      } @else {
        <div
          class="zag-skill-list-row__icon-avatar zag-icon-surface-gradient"
          [class.zag-skill-list-row__icon-avatar--fallback]="!skill().color"
          [style.background-color]="skill().color || null"
        >
          <span
            class="zag-skill-list-row__icon"
            [zagIcon]="skill().icon || 'psychology'"
            [style.color]="getIconColor(skill().color)"
          ></span>
        </div>
      }
      <div class="zag-skill-list-row__info">
        <span class="zag-skill-list-row__name">{{ skill().name }}</span>
        @if (skill().description) {
          <span class="zag-skill-list-row__description">{{ skill().description }}</span>
        }
      </div>
    </div>

    <div class="zag-skill-list-row__actions">
      @if (showDelete()) {
        <button
          type="button"
          class="zag-skill-list-row__action zag-skill-list-row__action--delete"
          [disabled]="deleteDisabled()"
          [attr.aria-label]="deleteAriaLabel() || null"
          (click)="onDeleteClick($event)"
        >
          <span zagIcon="delete"></span>
        </button>
      }
    </div>
  `,
  styles: [
    `
      .zag-skill-list-row {
        display: flex;
        align-items: center;
        padding: var(--spacing-sm) var(--spacing-md);
        gap: var(--spacing-md);
        min-width: 0;
        cursor: pointer;
        transition: box-shadow var(--transition-duration-fast, 0.15s) ease;
        background-color: var(--surface-color-default);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-md);

        &:focus-visible {
          outline: 2px solid var(--color-primary);
          outline-offset: 2px;
        }
      }

      .zag-skill-list-row__content {
        flex: 1;
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        min-width: 0;
      }

      .zag-skill-list-row__icon {
        font-size: var(--entity-avatar-icon-size-list);
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .zag-skill-list-row__flag {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        border-radius: var(--border-radius-sm);
        object-fit: cover;
        flex-shrink: 0;
        border: 1px solid var(--border-color-default);
      }

      .zag-skill-list-row__icon-avatar {
        width: var(--entity-avatar-size-list);
        height: var(--entity-avatar-size-list);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
      }

      .zag-skill-list-row__icon-avatar--fallback {
        background: linear-gradient(135deg, var(--color-primary), var(--color-primary-variant));
      }

      .zag-skill-list-row__info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 0;
      }

      .zag-skill-list-row__name {
        font-weight: 500;
        color: var(--text-color-primary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-skill-list-row__description {
        font-size: var(--font-size-sm);
        color: var(--text-color-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .zag-skill-list-row__actions {
        display: flex;
        align-items: center;
        gap: var(--spacing-xs);
        flex-shrink: 0;
      }

      .zag-skill-list-row__action {
        flex-shrink: 0;
        padding: var(--spacing-xs);
        color: var(--text-color-secondary);
        border: none;
        background: none;
        border-radius: var(--border-radius-sm);
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition:
          background-color var(--transition-duration-fast, 0.15s),
          color var(--transition-duration-fast, 0.15s);

        &:hover {
          background-color: var(--color-info-light);
          color: var(--color-primary);
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;

          &:hover {
            background-color: transparent;
            color: var(--text-color-secondary);
          }
        }

        [zagIcon] {
          font-size: 18px;
        }
      }

      .zag-skill-list-row__action--delete {
        &:hover:not(:disabled) {
          background-color: var(--color-error-light);
          color: var(--color-error);
        }
      }

      @media (max-width: 640px) {
        .zag-skill-list-row {
          flex-wrap: wrap;
          align-items: flex-start;
        }

        .zag-skill-list-row__content,
        .zag-skill-list-row__actions {
          width: 100%;
        }

        .zag-skill-list-row__description {
          overflow-wrap: anywhere;
          white-space: normal;
        }

        .zag-skill-list-row__actions {
          justify-content: flex-end;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .zag-skill-list-row {
          transition-duration: 0.01ms;
        }

        .zag-skill-list-row__action {
          transition-duration: 0.01ms;
        }
      }
    `,
  ],
})
export class SkillListRow {
  private readonly router = inject(Router);
  private readonly colorContrast = inject(ColorContrastService);

  /** The skill data to display */
  readonly skill = input.required<SkillListRowData>();

  /** Route for the edit link (array format for routerLink) */
  readonly editRoute = input<string[]>();

  /** Accessible label for the edit action */
  readonly editAriaLabel = input('');

  /** Whether to show the delete button */
  readonly showDelete = input(true);

  /** Accessible label for the delete action */
  readonly deleteAriaLabel = input('');

  /** Whether the delete button is disabled */
  readonly deleteDisabled = input(false);

  /** Emitted when the row is clicked */
  readonly rowClick = output<SkillListRowData>();

  /** Emitted when delete button is clicked */
  readonly deleteClick = output<SkillListRowData>();

  /** Handle row click - navigates to editRoute if set, then emits rowClick */
  onRowClick(): void {
    const route = this.editRoute();
    if (route) {
      this.router.navigate(route);
    }
    this.rowClick.emit(this.skill());
  }

  /** Handle keyboard activation for accessibility */
  onRowKeydown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('a') || target?.closest('button')) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onRowClick();
    }
  }

  /** Handle delete button click */
  onDeleteClick(event: Event): void {
    event.stopPropagation();
    this.deleteClick.emit(this.skill());
  }

  protected getIconColor(backgroundColor: string | null | undefined): string {
    return this.colorContrast.getIconColor(backgroundColor);
  }
}
